// Pinyin utilities: tone metadata, syllable splitting, and tone-mark stripping.
// Pure and dependency-free — the tone trainer and the card renderer both use this.

export interface ToneInfo {
  /** 1-4, plus 5 for the neutral tone. */
  n: number;
  name: string;
  /** How the pitch moves, in words. */
  contour: string;
  /** A physical cue that actually helps a beginner produce it. */
  cue: string;
  /** The classic minimal set, so all five can be compared on one syllable. */
  example: { hanzi: string; pinyin: string; meaning: string };
}

export const TONES: ToneInfo[] = [
  {
    n: 1,
    name: 'First — high level',
    contour: 'Starts high and stays there, flat.',
    cue: 'Hold a single steady note, like humming a long "mmm" at the top of your comfortable range.',
    example: { hanzi: '妈', pinyin: 'mā', meaning: 'mother' },
  },
  {
    n: 2,
    name: 'Second — rising',
    contour: 'Starts mid and rises to high.',
    cue: 'The way English lifts on a question: "Huh? Me?" That upward sweep is the second tone.',
    example: { hanzi: '麻', pinyin: 'má', meaning: 'hemp; numb' },
  },
  {
    n: 3,
    name: 'Third — low dipping',
    contour: 'Falls low, holds, and only rises at the very end.',
    cue: 'It is mostly a LOW tone, not a V. Let your voice drop and creak at the bottom; the rise only appears when the word stands alone.',
    example: { hanzi: '马', pinyin: 'mǎ', meaning: 'horse' },
  },
  {
    n: 4,
    name: 'Fourth — falling',
    contour: 'Starts high and drops sharply.',
    cue: 'Short and emphatic, like an annoyed English "No!" — it falls fast and ends abruptly.',
    example: { hanzi: '骂', pinyin: 'mà', meaning: 'to scold' },
  },
  {
    n: 5,
    name: 'Neutral — light and short',
    contour: 'No tone of its own; it takes its pitch from the syllable before it.',
    cue: 'Say it quickly and quietly, with no stress at all. Its pitch is whatever the previous tone left you on.',
    example: { hanzi: '吗', pinyin: 'ma', meaning: '(question marker)' },
  },
];

export function toneInfo(n: number): ToneInfo {
  return TONES.find((t) => t.n === n) ?? TONES[4]!;
}

/** Split "wǒ men" into ["wǒ", "men"]. Syllables arrive space-separated from the vocabulary build. */
export function syllablesOf(pinyin: string): string[] {
  return pinyin.trim().split(/\s+/u).filter(Boolean);
}

const TONE_MARKS: Record<string, string> = {
  ā: 'a', á: 'a', ǎ: 'a', à: 'a',
  ē: 'e', é: 'e', ě: 'e', è: 'e',
  ī: 'i', í: 'i', ǐ: 'i', ì: 'i',
  ō: 'o', ó: 'o', ǒ: 'o', ò: 'o',
  ū: 'u', ú: 'u', ǔ: 'u', ù: 'u',
  ǖ: 'ü', ǘ: 'ü', ǚ: 'ü', ǜ: 'ü',
};

/** "mā" -> "ma". Used by the tone drill, which must not give the answer away in the prompt. */
export function stripTones(pinyin: string): string {
  return [...pinyin].map((ch) => TONE_MARKS[ch] ?? TONE_MARKS[ch.toLowerCase()] ?? ch).join('');
}

/**
 * Tone-3 sandhi: a third tone before another third tone is said as a second tone.
 * 你好 is written nǐ hǎo and said ní hǎo. Returns the tones as they are actually SPOKEN,
 * which is what the card shows when you ask it how the word really sounds.
 */
export function spokenTones(tones: number[]): number[] {
  const out = [...tones];
  for (let i = 0; i < out.length - 1; i++) {
    if (out[i] === 3 && out[i + 1] === 3) out[i] = 2;
  }
  return out;
}

/** Does this word's pronunciation differ from its spelling because of tone-3 sandhi? */
export function hasSandhi(tones: number[]): boolean {
  return spokenTones(tones).some((t, i) => t !== tones[i]);
}

// ---------------------------------------------------------------- voice selection

/**
 * Apple ships a set of deliberately silly Chinese voices — cartoon characters, sped-up and
 * pitch-shifted. They are all tagged zh_CN, so anything that just takes the first zh-CN voice can
 * end up teaching pronunciation in Grandpa's voice. A pronunciation model has to be a straight
 * reading, so these are excluded from automatic selection entirely.
 */
const NOVELTY_VOICES = [
  'eddy', 'flo', 'grandma', 'grandpa', 'reed', 'rocko', 'sandy', 'shelley',
  'bubbles', 'jester', 'organ', 'superstar', 'trinoids', 'whisper', 'wobble', 'zarvox',
  'albert', 'bad news', 'bahh', 'bells', 'boing', 'good news', 'junior', 'kathy', 'ralph',
];

export interface VoiceLike {
  name: string;
  lang: string;
  voiceURI: string;
  localService?: boolean;
}

export type VoiceQuality = 'premium' | 'enhanced' | 'compact' | 'novelty';

/**
 * macOS exposes a voice's grade in its NAME — "Tingting (Premium)", "Meijia (Enhanced)" — while a
 * bare name is the compact, robotic-sounding build. That string is the only quality signal the Web
 * Speech API gives us, so it is what ranking has to read.
 */
export function voiceQuality(voice: VoiceLike): VoiceQuality {
  const name = voice.name.toLowerCase();
  // Match the base name so "Sandy (Premium)" is still the novelty voice Sandy.
  const base = name.replace(/\s*\((?:premium|enhanced|compact)\)\s*/u, '').replace(/\s*\(.*\)\s*$/u, '').trim();
  if (NOVELTY_VOICES.includes(base)) return 'novelty';
  if (name.includes('premium')) return 'premium';
  if (name.includes('enhanced')) return 'enhanced';
  return 'compact';
}

const QUALITY_RANK: Record<VoiceQuality, number> = { premium: 3, enhanced: 2, compact: 1, novelty: 0 };

/**
 * How good a voice is, with no reference to what language it speaks.
 *
 * Split out from scoreVoice because the two halves are about different things: quality is a fact
 * about the voice, and preferring one accent over another is a fact about the LANGUAGE. Every
 * language wants the first half; only Mandarin wants the second. Novelty voices score zero, which
 * is below anything usable, wherever they turn up.
 */
export function qualityScore(voice: VoiceLike): number {
  const quality = voiceQuality(voice);
  return quality === 'novelty' ? 0 : QUALITY_RANK[quality] * 10;
}

/**
 * Score a voice for reading Mandarin vocabulary. Higher is better; anything at or below zero is
 * unusable and should not be auto-selected.
 *
 * Quality dominates, because the gap between Apple's compact and enhanced builds is far larger
 * than any accent preference. Mainland zh-CN is then preferred over zh-TW and zh-HK: the decks are
 * simplified-script and pinyin-based, and a Cantonese (zh-HK) voice would read them with entirely
 * the wrong pronunciation.
 */
export function scoreVoice(voice: VoiceLike): number {
  const quality = voiceQuality(voice);
  if (quality === 'novelty') return -1;

  const lang = voice.lang.toLowerCase().replace('_', '-');
  if (!lang.startsWith('zh')) return -1;

  let score = QUALITY_RANK[quality] * 10;
  if (lang.startsWith('zh-cn')) score += 5;
  else if (lang.startsWith('zh-tw')) score += 2; // Mandarin, different accent and script norms
  else if (lang.startsWith('zh-hk')) score += 0; // usually Cantonese — wrong language for these decks
  else score += 1;
  return score;
}

/** Usable Chinese voices, best first. Novelty and non-Chinese voices are dropped. */
export function rankVoices<T extends VoiceLike>(voices: T[]): T[] {
  return voices
    .filter((v) => scoreVoice(v) > 0)
    .sort((a, b) => scoreVoice(b) - scoreVoice(a) || a.name.localeCompare(b.name));
}

/** The best available Chinese voice, or undefined when none is usable. */
export function bestVoice<T extends VoiceLike>(voices: T[]): T | undefined {
  return rankVoices(voices)[0];
}
